import {
  React,
  useState,
  useRef,
  useAuth,
  useEffect,
  useClientInfo,
  axiosInstance,
  convertDDMMYYYYtoISO,
  DocumentManager,
  CategoryDropdown,
  TariffManager,
  ContractTariffManager,
  SnackbarAlert,
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  FormControl,
  MenuItem,
  Checkbox,
  IconButton,
  FormControlLabel,
  Radio,
  RadioGroup,
  DeleteIcon,
  EditIcon,
} from "../../utils/commonImports";

/* ------------------------------------------------------------------ */
/* Defaults                                                            */
/* ------------------------------------------------------------------ */
const initialForm = {
  // Main Section
  client_id: "",
  customertype: "",
  customername: "",
  customercompanyname: "",
  customerprimarycontactname: "",
  customerprimarycontactemailaddress: "",
  customerprimarycontactphone: "",
  customeractivestatus: "Active",
  // Address
  customerbillingcountry: "",
  customerbillingaddressstreet1: "",
  customerbillingaddressstreet2: "",
  customerbillingcity: "",
  customerbillingstate: "",
  customerbillingpincode: "",
  customerbillingphone: "",
  customersameasbillingaddres: false,
  customerservicecountry: "",
  customerserviceaddressstreet1: "",
  customerserviceaddressstreet2: "",
  customerservicecity: "",
  customerservicestate: "",
  customerservicepincode: "",
  customerservicephone: "",
  // GST/Tax
  customergsttreament: "",
  customertaxpreference: "",
  customertaxexemptedreason: "",
  customergstin: "",
  customerplaceofsupply: "",
  customerapplygstparkingtollpermit: "",
  customerapplygstbatta: "",
  customerapplygstothers: "",
  customerpanno: "",
  customerservicestartdate: "",
  customerpaymentterms: "",
  customercurrency: "",
  customercreditlimit: "",
  customeropeningbalance: "",
  customerportalenable: false,
  customerremarks: "",
  customerrequiredsc: false,
};

// Contact defaults (single source of truth)
const initialContactDraft = {
  customercontactsalutation: "",
  customercontactfirstname: "",
  customercontactlastname: "",
  customercontactemail: "",
  customercontactworkphone: "",
  customercontactmobile: "",
  customercontactdesignation: "",
  customercontactdepartment: "",
  customercontactcontacttype: "",
  customercontactprimary: false,
};

const TABS = [
  "Address",
  "Tax/GST",
  "Contacts",
  "Documents",
  "Tariff",
  "Monthly Contract Tariff",
];

const locationData = {
  India: {
    TamilNadu: ["Chennai", "Madurai"],
    Karnataka: ["Bangalore", "Mysore"],
  },
  USA: {
    California: ["Los Angeles", "San Francisco"],
    Texas: ["Houston", "Dallas"],
  },
};

/* ------------------------------------------------------------------ */
/* Compact design tokens                                               */
/* ------------------------------------------------------------------ */
const C = {
  primary: "#1d4ed8",
  primaryHover: "#1e40af",
  border: "#e2e8f0",
  surface: "#f8fafc",
  text: "#0f172a",
  muted: "#64748b",
};

// Dense input styling shared by every field
const fieldSx = {
  "& .MuiInputBase-root": { fontSize: 13, backgroundColor: "#fff" },
  "& .MuiInputBase-input": { py: "7px" },
  "& .MuiSelect-select": { py: "7px !important" },
  "& .MuiInputLabel-root": { fontSize: 13 },
  "& .MuiInputLabel-shrink": { fontSize: 13 },
  "& .MuiFormHelperText-root": { fontSize: 11, mx: 0 },
};

const btnBase = {
  textTransform: "none",
  fontSize: 13,
  fontWeight: 500,
  borderRadius: "6px",
  boxShadow: "none",
  minWidth: 0,
  px: 2,
  py: 0.6,
};

/* ------------------------------------------------------------------ */
/* Small presentational helpers                                        */
/* ------------------------------------------------------------------ */
const Card = ({ title, action, children, sx }) => (
  <Box
    sx={{
      border: `1px solid ${C.border}`,
      borderRadius: "8px",
      backgroundColor: "#fff",
      ...sx,
    }}
  >
    {(title || action) && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          minHeight: 40,
          borderBottom: `1px solid ${C.border}`,
          backgroundColor: C.surface,
          borderRadius: "8px 8px 0 0",
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>
          {title}
        </Typography>
        {action}
      </Box>
    )}
    <Box sx={{ p: 2 }}>{children}</Box>
  </Box>
);

const Grid = ({ cols = 3, children, sx }) => (
  <Box
    sx={{
      display: "grid",
      gap: 1.5,
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, 1fr)",
        md: `repeat(${cols}, 1fr)`,
      },
      ...sx,
    }}
  >
    {children}
  </Box>
);

// Text input / select with consistent dense styling
const Field = ({ options, children, sx, ...props }) => (
  <TextField
    size="small"
    fullWidth
    select={!!options}
    sx={{ ...fieldSx, ...sx }}
    {...props}
  >
    {options &&
      options.map((o) => {
        const opt = typeof o === "string" ? { value: o, label: o } : o;
        return (
          <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
            {opt.label}
          </MenuItem>
        );
      })}
    {children}
  </TextField>
);

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
export default function CustomerFormAdd({
  open = true,
  onCancel,
  onSuccess,
  editData,
  customerId,
}) {
  const { clientId } = useClientInfo();
  const { token } = useAuth();

  const [activeTab, setActiveTab] = useState("Address");
  const [saving, setSaving] = useState(false);

  const tariffManagerRef = useRef(null);
  const contractTariffRef = useRef();

  const [form, setForm] = useState(initialForm);
  const [editingIndex, setEditingIndex] = useState(-1);

  const [documents, setDocuments] = useState([]);
  const [deletedDocumentIds, setDeletedDocumentIds] = useState([]);

  const [contacts, setContacts] = useState([]);
  const [contactDraft, setContactDraft] = useState(initialContactDraft);
  const [deletedContactIds, setDeletedContactIds] = useState([]);

  const [tariffs, setTariffs] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);

  // Callback to get documents + deleted IDs from DocumentManager
  const handleDocumentsChange = (docs, deletedIds) => {
    setDocuments(docs);
    setDeletedDocumentIds(deletedIds);
  };

  const handleTariffsChange = (updatedTariffs) => setTariffs(updatedTariffs);

  // Format date helper
  const formatDateToDDMMYYYY = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1,
    ).padStart(2, "0")}-${d.getFullYear()}`;
  };

  /* ---------------------------- Effects ---------------------------- */
  useEffect(() => {
    async function fetchTariffs() {
      if (!clientId || !customerId) return;
      try {
        const response = await axiosInstance.get(
          `/api/tariffs/customer/${customerId}?client_id=${clientId}`,
        );
        setTariffs(response.data);
      } catch (err) {
        console.error(err);
      }
    }
    fetchTariffs();
  }, [clientId, customerId]);

  useEffect(() => {
    fetch("/api/category?categorytype=CustomerDocument")
      .then((res) => res.json())
      .then((data) => setDocumentCategories(data))
      .catch((err) =>
        console.error("Failed to fetch customer document categories", err),
      );
  }, []);

  useEffect(() => {
    if (editData) {
      // Edit mode — merge editData with initialForm
      const formatted = { ...initialForm, ...editData };

      // Patch client_id from context to guarantee correct scoping
      formatted.client_id = clientId || formatted.client_id;

      if (formatted.customerservicestartdate) {
        formatted.customerservicestartdate = formatDateToDDMMYYYY(
          formatted.customerservicestartdate,
        );
      }

      setForm(formatted);
      setContacts(Array.isArray(editData.contacts) ? editData.contacts : []);
    } else {
      setForm({ ...initialForm, client_id: clientId || "" });
      setContacts([]); // documents are managed inside DocumentManager
    }
  }, [editData, open, clientId]);

  /* --------------------------- Handlers ---------------------------- */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Clear reason if changing to Taxable
    if (name === "customertaxpreference" && value === "Taxable") {
      setForm((f) => ({
        ...f,
        [name]: value,
        customertaxexemptedreason: "",
      }));
      return;
    }

    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSameAsBilling = (checked) => {
    if (checked) {
      setForm((prev) => ({
        ...prev,
        customersameasbillingaddres: true,
        customerservicecountry: prev.customerbillingcountry,
        customerserviceaddressstreet1: prev.customerbillingaddressstreet1,
        customerserviceaddressstreet2: prev.customerbillingaddressstreet2,
        customerservicecity: prev.customerbillingcity,
        customerservicestate: prev.customerbillingstate,
        customerservicepincode: prev.customerbillingpincode,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        customersameasbillingaddres: false,
        customerservicecountry: "",
        customerserviceaddressstreet1: "",
        customerserviceaddressstreet2: "",
        customerservicecity: "",
        customerservicestate: "",
        customerservicepincode: "",
      }));
    }
  };

  // Contact draft helpers
  const setDraft = (name) => (e) =>
    setContactDraft((d) => ({ ...d, [name]: e.target.value }));

  const handleContactSubmit = () => {
    if (editingIndex !== -1) {
      setContacts((cs) =>
        cs.map((c, i) => (i === editingIndex ? { ...contactDraft } : c)),
      );
      setEditingIndex(-1);
    } else {
      setContacts((cs) => [
        ...cs,
        {
          ...contactDraft,
          customercontactprimary:
            contactDraft.customercontactprimary === "Yes" ||
            contactDraft.customercontactprimary === true,
        },
      ]);
    }
    setContactDraft(initialContactDraft);
  };

  const handleContactCancel = () => {
    setEditingIndex(-1);
    setContactDraft(initialContactDraft);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let customerId = editData?.customerid;

      // Prepare payload
      let payload = { ...form };
      if (payload.customerservicestartdate) {
        payload.customerservicestartdate = convertDDMMYYYYtoISO(
          payload.customerservicestartdate,
        );
      }
      payload.client_id = clientId;

      // STEP 1: SAVE / UPDATE CUSTOMER
      if (!customerId) {
        const customerResponse = await axiosInstance.post(
          `/api/customers`,
          payload,
        );
        customerId = customerResponse.data.customerid;
      } else {
        await axiosInstance.put(`/api/customers/${customerId}`, payload);
      }

      // STEP 2: SAVE CONTACTS
      if (contacts?.length > 0 || deletedContactIds?.length > 0) {
        await axiosInstance.post(
          `/api/customercontacts/customer/${customerId}/contacts/batch-save`,
          {
            contactsRaw: JSON.stringify(contacts),
            deletedContactIdsRaw: JSON.stringify(deletedContactIds),
            client_id: clientId,
          },
        );
        setDeletedContactIds([]);
      }

      // STEP 3: SAVE TARIFFS (old tariff module)
      if (tariffs?.length > 0) {
        await axiosInstance.post(`/api/tariffs/sync/customer/${customerId}`, {
          tariffs,
          client_id: clientId,
        });
      }

      // STEP 4: SAVE CONTRACT TARIFFS (new module)
      let contractTariffs = [];
      if (contractTariffRef?.current?.getTariffs) {
        contractTariffs = contractTariffRef.current.getTariffs();
      }
      if (contractTariffs.length > 0) {
        await axiosInstance.post("/api/tariff-contract", {
          client_id: clientId,
          customer_id: customerId,
          tariffs: contractTariffs,
        });
      }

      // STEP 5: DELETE DOCUMENTS
      if (deletedDocumentIds?.length > 0) {
        await axiosInstance.post(`/api/documents/delete`, {
          documentIds: deletedDocumentIds,
        });
      }

      // STEP 6: SAVE DOCUMENTS
      if (documents.length > 0) {
        const formData = new FormData();
        formData.append("documentsMeta", JSON.stringify(documents));
        documents.forEach((doc) => {
          if (doc.file instanceof File) {
            formData.append("documentFiles", doc.file);
          }
        });
        formData.append("client_id", clientId);

        await axiosInstance.post(
          `/api/documents/customer/${customerId}/batch-save/${clientId}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
      }

      SnackbarAlert(
        editData?.customerid
          ? "Customer updated successfully"
          : "Customer created successfully",
        "success",
      );

      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("handleSave error details:", error);

      const status = error?.response?.status;
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Unexpected error";

      if (status === 403) {
        SnackbarAlert(msg, "error");
        return;
      }

      SnackbarAlert(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  /* --------------------------- Tab badges -------------------------- */
  const tabCount = {
    Contacts: contacts.length,
    Documents: documents.length,
    Tariff: tariffs.length,
  };

  /* --------------------------- Address card ------------------------ */
  const renderAddressCard = (section) => {
    const isService = section === "Service";
    const prefix = isService ? "customerservice" : "customerbilling";
    const sameAsBilling = isService && form.customersameasbillingaddres;

    const countries = Object.keys(locationData);
    const selectedCountry = form[`${prefix}country`];
    const states = selectedCountry
      ? Object.keys(locationData[selectedCountry] || {})
      : [];
    const selectedState = form[`${prefix}state`];
    const cities =
      selectedCountry && selectedState
        ? locationData[selectedCountry]?.[selectedState] || []
        : [];

    return (
      <Card
        key={section}
        title={`${section} address`}
        action={
          isService && (
            <FormControlLabel
              sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 12 } }}
              control={
                <Checkbox
                  size="small"
                  sx={{ p: 0.5 }}
                  checked={!!form.customersameasbillingaddres}
                  onChange={(e) => handleSameAsBilling(e.target.checked)}
                />
              }
              label="Same as billing"
            />
          )
        }
      >
        <Grid cols={2}>
          <Field
            label="Country/Region"
            name={`${prefix}country`}
            value={form[`${prefix}country`] || ""}
            disabled={sameAsBilling}
            onChange={(e) => {
              const value = e.target.value;
              setForm((prev) => ({
                ...prev,
                [`${prefix}country`]: value,
                [`${prefix}state`]: "",
                [`${prefix}city`]: "",
              }));
            }}
            options={["", ...countries].map((c) => ({
              value: c,
              label: c || "Select country",
            }))}
          />
          <Field
            label="State"
            name={`${prefix}state`}
            value={form[`${prefix}state`] || ""}
            disabled={sameAsBilling || !selectedCountry}
            onChange={(e) => {
              const value = e.target.value;
              setForm((prev) => ({
                ...prev,
                [`${prefix}state`]: value,
                [`${prefix}city`]: "",
              }));
            }}
            options={["", ...states].map((s) => ({
              value: s,
              label: s || "Select state",
            }))}
          />
          <Field
            label="City"
            name={`${prefix}city`}
            value={form[`${prefix}city`] || ""}
            disabled={sameAsBilling || !selectedState}
            onChange={handleChange}
            options={["", ...cities].map((c) => ({
              value: c,
              label: c || "Select city",
            }))}
          />
          <Field
            label="Pincode"
            name={`${prefix}pincode`}
            value={form[`${prefix}pincode`] || ""}
            onChange={handleChange}
            disabled={sameAsBilling}
          />
          <Field
            label="Street 1"
            name={`${prefix}addressstreet1`}
            value={form[`${prefix}addressstreet1`] || ""}
            onChange={handleChange}
            disabled={sameAsBilling}
            sx={{ gridColumn: { sm: "span 2" } }}
          />
          <Field
            label="Street 2"
            name={`${prefix}addressstreet2`}
            value={form[`${prefix}addressstreet2`] || ""}
            onChange={handleChange}
            disabled={sameAsBilling}
            sx={{ gridColumn: { sm: "span 2" } }}
          />
        </Grid>
      </Card>
    );
  };

  const yesNo = [
    { value: "", label: "None" },
    { value: "Yes", label: "Yes" },
    { value: "No", label: "No" },
  ];

  /* ------------------------------ UI ------------------------------- */
  return (
    <Paper
      elevation={0}
      sx={{
        mx: "auto",
        my: 1,
        maxWidth: 1400,
        borderRadius: "10px",
        border: `1px solid ${C.border}`,
        minHeight: "85vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* ---------- Header ---------- */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 1.5,
          borderBottom: `1px solid ${C.border}`,
          backgroundColor: C.surface,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 17, fontWeight: 700, color: C.text }}>
            {editData ? "Edit customer" : "New customer"}
          </Typography>
          <Typography sx={{ fontSize: 12, color: C.muted }}>
            Fields marked * are required
          </Typography>
        </Box>
      </Box>

      <Box sx={{ p: 3, flex: 1 }}>
        {/* ---------- Basic details ---------- */}
        <Card title="Basic details">
          <Grid cols={4}>
            <Field
              required
              label="Customer type"
              name="customertype"
              value={form.customertype}
              onChange={handleChange}
              options={["Business", "Individual"]}
            />
            <Field
              required
              label="Customer name"
              name="customername"
              value={form.customername}
              onChange={handleChange}
            />
            {/* Company Name kept hidden, as in the original */}
            <Box sx={{ display: "none" }}>
              <Field
                label="Company name"
                name="customercompanyname"
                value={form.customercompanyname}
                onChange={handleChange}
              />
            </Box>
            <Field
              label="Primary contact name"
              name="customerprimarycontactname"
              value={form.customerprimarycontactname}
              onChange={handleChange}
            />
            <Field
              required
              type="email"
              label="Primary contact email"
              name="customerprimarycontactemailaddress"
              value={form.customerprimarycontactemailaddress}
              onChange={handleChange}
            />
            <Field
              label="Primary contact phone"
              name="customerprimarycontactphone"
              value={form.customerprimarycontactphone}
              onChange={handleChange}
            />
            <Field
              required
              label="Status"
              name="customeractivestatus"
              value={form.customeractivestatus}
              onChange={handleChange}
              options={["Active", "Inactive"]}
            />
          </Grid>
        </Card>

        {/* ---------- Tab bar ---------- */}
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            mt: 3,
            mb: 2,
            borderBottom: `1px solid ${C.border}`,
            overflowX: "auto",
          }}
        >
          {TABS.map((tab) => {
            const active = activeTab === tab;
            const count = tabCount[tab];
            return (
              <button
                type="button"
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: "8px 14px",
                  fontSize: 13,
                  fontWeight: active ? 600 : 500,
                  color: active ? C.primary : C.muted,
                  background: "none",
                  border: "none",
                  borderBottom: `2px solid ${
                    active ? C.primary : "transparent"
                  }`,
                  marginBottom: -1,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {tab}
                {count > 0 && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      lineHeight: 1,
                      padding: "3px 6px",
                      borderRadius: 10,
                      color: active ? C.primary : C.muted,
                      background: active ? "#dbeafe" : "#f1f5f9",
                    }}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </Box>

        {/* ---------- ADDRESS TAB ---------- */}
        {activeTab === "Address" && (
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            }}
          >
            {["Billing", "Service"].map(renderAddressCard)}
          </Box>
        )}

        {/* ---------- TAX / GST TAB ---------- */}
        {activeTab === "Tax/GST" && (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Card title="GST details">
              <Grid cols={3}>
                <CategoryDropdown
                  categoryType="GST"
                  name="customergsttreament"
                  label="GST Treatment"
                  value={form.customergsttreament}
                  onChange={handleChange}
                />
                <Field
                  label="GSTIN"
                  name="customergstin"
                  value={form.customergstin}
                  onChange={handleChange}
                />
                <Field
                  label="PAN no."
                  name="customerpanno"
                  value={form.customerpanno}
                  onChange={handleChange}
                />
                <Field
                  label="Place of supply"
                  name="customerplaceofsupply"
                  value={form.customerplaceofsupply}
                  onChange={handleChange}
                  options={[
                    { value: "", label: "None" },
                    { value: "TamilNadu", label: "Tamil Nadu" },
                    { value: "Karnataka", label: "Karnataka" },
                    { value: "Maharashtra", label: "Maharashtra" },
                  ]}
                />

                {/* Tax preference with inline exempted reason */}
                <FormControl
                  sx={{
                    gridColumn: { md: "span 2" },
                    flexDirection: "row",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1.5,
                  }}
                >
                  <Typography sx={{ fontSize: 13, color: C.muted }}>
                    Tax preference
                  </Typography>
                  <RadioGroup
                    row
                    name="customertaxpreference"
                    value={form.customertaxpreference || ""}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="Taxable"
                      control={<Radio size="small" />}
                      label="Taxable"
                      sx={{ "& .MuiFormControlLabel-label": { fontSize: 13 } }}
                    />
                    <FormControlLabel
                      value="Tax Exempt"
                      control={<Radio size="small" />}
                      label="Tax exempt"
                      sx={{ "& .MuiFormControlLabel-label": { fontSize: 13 } }}
                    />
                  </RadioGroup>
                  {form.customertaxpreference === "Tax Exempt" && (
                    <TextField
                      required
                      size="small"
                      name="customertaxexemptedreason"
                      label="Exempted reason"
                      value={form.customertaxexemptedreason}
                      onChange={handleChange}
                      sx={{ ...fieldSx, width: 240 }}
                    />
                  )}
                </FormControl>

                <Field
                  label="GST on parking / toll / permit"
                  name="customerapplygstparkingtollpermit"
                  value={form.customerapplygstparkingtollpermit}
                  onChange={handleChange}
                  options={yesNo}
                />
                <Field
                  label="GST on batta"
                  name="customerapplygstbatta"
                  value={form.customerapplygstbatta}
                  onChange={handleChange}
                  options={yesNo}
                />
                <Field
                  label="GST on others"
                  name="customerapplygstothers"
                  value={form.customerapplygstothers}
                  onChange={handleChange}
                  options={yesNo}
                />
              </Grid>
            </Card>

            <Card title="Billing & account">
              <Grid cols={4}>
                <Field
                  label="Payment terms"
                  name="customerpaymentterms"
                  value={form.customerpaymentterms || ""}
                  onChange={handleChange}
                  options={[
                    { value: "", label: "None" },
                    "Net 7",
                    "Net 15",
                    "Net 30",
                    "Net 45",
                    "Due on Receipt",
                  ]}
                />
                <Field
                  label="Currency"
                  name="customercurrency"
                  value={form.customercurrency || ""}
                  onChange={handleChange}
                  options={[{ value: "", label: "None" }, "INR", "USD", "EUR"]}
                />
                <Field
                  type="number"
                  label="Credit limit"
                  name="customercreditlimit"
                  value={form.customercreditlimit || ""}
                  onChange={handleChange}
                />
                <Field
                  type="number"
                  label="Opening balance"
                  name="customeropeningbalance"
                  value={form.customeropeningbalance || ""}
                  onChange={handleChange}
                />
                <Field
                  label="Service start date"
                  name="customerservicestartdate"
                  placeholder="dd-MM-yyyy"
                  value={form.customerservicestartdate || ""}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
                <FormControlLabel
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": { fontSize: 13 },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      name="customerportalenable"
                      checked={!!form.customerportalenable}
                      onChange={handleChange}
                    />
                  }
                  label="Enable portal access"
                />
                <Field
                  multiline
                  rows={2}
                  label="Remarks / notes"
                  name="customerremarks"
                  value={form.customerremarks || ""}
                  onChange={handleChange}
                  sx={{ gridColumn: { md: "1 / -1" } }}
                />
                <FormControlLabel
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: 13,
                    },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      name="customerrequiredsc"
                      checked={!!form.customerrequiredsc}
                      onChange={handleChange}
                    />
                  }
                  label="Require DSC on invoice"
                />
              </Grid>
            </Card>
          </Box>
        )}

        {/* ---------- CONTACTS TAB ---------- */}
        {activeTab === "Contacts" && (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Card
              title={editingIndex !== -1 ? "Edit contact" : "Add contact"}
              action={
                <Box sx={{ display: "flex", gap: 1 }}>
                  {editingIndex !== -1 && (
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={handleContactCancel}
                      sx={{ ...btnBase, borderColor: C.border }}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleContactSubmit}
                    disabled={
                      !contactDraft.customercontactfirstname &&
                      !contactDraft.customercontactemail
                    }
                    sx={{
                      ...btnBase,
                      backgroundColor: C.primary,
                      "&:hover": { backgroundColor: C.primaryHover },
                    }}
                  >
                    {editingIndex !== -1 ? "Save contact" : "Add contact"}
                  </Button>
                </Box>
              }
            >
              <Grid cols={4}>
                <Field
                  label="Salutation"
                  value={contactDraft.customercontactsalutation || ""}
                  onChange={setDraft("customercontactsalutation")}
                  options={["Mr.", "Mrs.", "Ms.", "Dr.", "Prof."]}
                />
                <Field
                  label="First name"
                  value={contactDraft.customercontactfirstname || ""}
                  onChange={setDraft("customercontactfirstname")}
                />
                <Field
                  label="Last name"
                  value={contactDraft.customercontactlastname || ""}
                  onChange={setDraft("customercontactlastname")}
                />
                <Field
                  label="Email"
                  value={contactDraft.customercontactemail || ""}
                  onChange={setDraft("customercontactemail")}
                />
                <Field
                  label="Work phone"
                  value={contactDraft.customercontactworkphone || ""}
                  onChange={setDraft("customercontactworkphone")}
                />
                <Field
                  label="Mobile"
                  value={contactDraft.customercontactmobile || ""}
                  onChange={setDraft("customercontactmobile")}
                />
                <Field
                  label="Designation"
                  value={contactDraft.customercontactdesignation || ""}
                  onChange={setDraft("customercontactdesignation")}
                />
                <Field
                  label="Department"
                  value={contactDraft.customercontactdepartment || ""}
                  onChange={setDraft("customercontactdepartment")}
                />
                <Field
                  label="Contact type"
                  value={contactDraft.customercontactcontacttype || ""}
                  onChange={setDraft("customercontactcontacttype")}
                  options={[
                    "Orderby",
                    "Userby",
                    "Admin",
                    "Accounts",
                    "HR",
                    "Purchase",
                  ]}
                />
                <FormControlLabel
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": { fontSize: 13 },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={
                        contactDraft.customercontactprimary === true ||
                        contactDraft.customercontactprimary === "Yes"
                      }
                      onChange={(e) =>
                        setContactDraft((d) => ({
                          ...d,
                          customercontactprimary: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Primary contact"
                />
              </Grid>
            </Card>

            {/* Contacts table */}
            <Box
              sx={{
                border: `1px solid ${C.border}`,
                borderRadius: "8px",
                overflowX: "auto",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: C.surface }}>
                    {[
                      "Name",
                      "Email",
                      "Work phone",
                      "Mobile",
                      "Designation",
                      "Department",
                      "Type",
                      "Primary",
                      "",
                    ].map((h) => (
                      <th
                        key={h || "actions"}
                        style={{
                          textAlign: "left",
                          fontWeight: 600,
                          color: C.muted,
                          padding: "8px 10px",
                          borderBottom: `1px solid ${C.border}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {contacts.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        style={{
                          textAlign: "center",
                          color: C.muted,
                          padding: 20,
                        }}
                      >
                        No contacts yet. Fill in the form above and click “Add
                        contact”.
                      </td>
                    </tr>
                  ) : (
                    contacts.map((contact, i) => (
                      <tr
                        key={i}
                        style={{
                          borderBottom: `1px solid ${C.border}`,
                          backgroundColor:
                            editingIndex === i ? "#eff6ff" : "transparent",
                        }}
                      >
                        <td style={{ padding: "6px 10px", fontWeight: 500 }}>
                          {[
                            contact.customercontactsalutation,
                            contact.customercontactfirstname,
                            contact.customercontactlastname,
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        </td>
                        <td style={{ padding: "6px 10px" }}>
                          {contact.customercontactemail}
                        </td>
                        <td style={{ padding: "6px 10px" }}>
                          {contact.customercontactworkphone}
                        </td>
                        <td style={{ padding: "6px 10px" }}>
                          {contact.customercontactmobile}
                        </td>
                        <td style={{ padding: "6px 10px" }}>
                          {contact.customercontactdesignation}
                        </td>
                        <td style={{ padding: "6px 10px" }}>
                          {contact.customercontactdepartment}
                        </td>
                        <td style={{ padding: "6px 10px" }}>
                          {contact.customercontactcontacttype}
                        </td>
                        <td style={{ padding: "6px 10px" }}>
                          {contact.customercontactprimary ? (
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                padding: "2px 8px",
                                borderRadius: 10,
                                color: "#166534",
                                background: "#dcfce7",
                              }}
                            >
                              Primary
                            </span>
                          ) : (
                            <span style={{ color: C.muted }}>—</span>
                          )}
                        </td>
                        <td
                          style={{ padding: "2px 6px", whiteSpace: "nowrap" }}
                        >
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setContactDraft({
                                ...initialContactDraft,
                                ...contact,
                              });
                              setEditingIndex(i);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              setContacts((cs) => cs.filter((_, j) => j !== i))
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </Box>
          </Box>
        )}

        {/* ---------- DOCUMENTS TAB ---------- */}
        {activeTab === "Documents" && (
          <DocumentManager
            entityType="customer"
            entityId={editData?.customerid || form.client_id}
            clientId={clientId}
            onDocumentsChange={handleDocumentsChange}
            documentCategoryType="CustomerDocument" // filters dropdown options
          />
        )}

        {/* ---------- TARIFF TAB ---------- */}
        {activeTab === "Tariff" && (
          <TariffManager
            entityType="customer"
            entityId={editData?.customerid || form.client_id}
            clientId={clientId}
            token={token}
            readOnly={false}
            onTariffsChange={handleTariffsChange}
            ref={tariffManagerRef}
          />
        )}

        {/* ---------- MONTHLY CONTRACT TARIFF TAB ---------- */}
        {activeTab === "Monthly Contract Tariff" && (
          <ContractTariffManager
            ref={contractTariffRef}
            clientId={clientId}
            customerId={editData?.customerid}
            token={token}
          />
        )}
      </Box>

      {/* ---------- Sticky action bar ---------- */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          display: "flex",
          gap: 1,
          justifyContent: "flex-end",
          px: 3,
          py: 1.5,
          borderTop: `1px solid ${C.border}`,
          backgroundColor: "#fff",
        }}
      >
        <Button
          onClick={onCancel}
          variant="outlined"
          color="inherit"
          disabled={saving}
          sx={{ ...btnBase, borderColor: C.border, color: C.muted }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          sx={{
            ...btnBase,
            backgroundColor: C.primary,
            "&:hover": { backgroundColor: C.primaryHover },
          }}
        >
          {saving ? "Saving…" : editData ? "Update customer" : "Save customer"}
        </Button>
      </Box>
    </Paper>
  );
}
